import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { RefreshCw, Calendar, Download } from 'lucide-react';
import DatePickerButton from '../ui/date-picker-button';
import apiService from '../../services/api';
import { API_CONFIG } from '../../config/api';
import EnergyLossWaterfall from '../charts/EnergyLossWaterfall';
import moment from 'moment';
import { useToast } from '../../hooks/use-toast';
import { ResponsiveContainer, ComposedChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, Cell, LabelList } from 'recharts';

// Plant data structure matching Angular app
const plantData = [
  { No: 1, kpi: "Designed Energy Export", level: "Plant", Type: "--" },
  { No: 2, kpi: "Irradiation Energy", level: "Weather Station", Type: "Non-Recoverable" },
  { No: 3, kpi: "Shading", level: "Plant", Type: "Non-Recoverable" },
  { No: 4, kpi: "Soiling", level: "Plant", Type: "Recoverable" },
  { No: 5, kpi: "IAM Loss Factor*", level: "Plant", Type: "Designed Loss" },
  { No: 6, kpi: "Corrected Energy Export", level: "Plant", Type: "--" },
  { No: 7, kpi: "Module Degradation*", level: "Plant", Type: "Designed Loss" },
  { No: 8, kpi: "PV Module Temperature", level: "Plant", Type: "Non-Recoverable" },
  { No: 9, kpi: "LID Loss*", level: "Plant", Type: "Designed Loss" },
  { No: 10, kpi: "Mismatch Loss*", level: "Plant", Type: "Designed Loss" },
  { No: 11, kpi: "DC Cable Loss*", level: "Plant", Type: "Designed Loss" },
  { No: 12, kpi: "Inverter Input", level: "Plant", Type: "--" },
  { No: 13, kpi: "Inverter Efficiency", level: "Plant", Type: "Non-Recoverable" },
  { No: 14, kpi: "Inverter Clipping", level: "Plant", Type: "Non-Recoverable" },
  { No: 15, kpi: "Inverter Deration", level: "Plant", Type: "Non-Recoverable" },
  { No: 16, kpi: "Inverter Output", level: "Plant", Type: "--" },
  { No: 17, kpi: "AC Loss**", level: "Plant", Type: "Designed Loss" },
  { No: 18, kpi: "Plant Unavailability", level: "Plant", Type: "Non-Recoverable" },
  { No: 19, kpi: "Grid Unavailability", level: "Plant", Type: "Non-Recoverable" },
  { No: 20, kpi: "Aux Consumption", level: "Plant", Type: "Non-Recoverable" },
  { No: 21, kpi: "Other Losses", level: "Plant", Type: "Non-Recoverable" },
  { No: 22, kpi: "Actual Energy Export", level: "Plant", Type: "--" }
];

const EnergyLoss = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [waterfallChartData, setWaterfallChartData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [summaryData, setSummaryData] = useState({
    actualEE: 0, targetEE: 0, recoverableEnergy: 0, nonRecoverableEnergy: 0, irr: 0
  });
  
  // Use current date as default
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yestStr = yesterday.toLocaleDateString('en-CA');
  const [startDate, setStartDate] = useState(yestStr);
  const [endDate, setEndDate] = useState(yestStr);
  
  const [selectedKPIs, setSelectedKPIs] = useState([
    "Designed Energy Export", "Irradiation Energy", "Shading", "Soiling", "IAM Loss Factor*",
    "Corrected Energy Export", "Module Degradation*", "PV Module Temperature", "LID Loss*",
    "Mismatch Loss*", "DC Cable Loss*", "Inverter Input", "Inverter Efficiency",
    "Inverter Clipping", "Inverter Deration", "Inverter Output", "AC Loss**",
    "Plant Unavailability", "Grid Unavailability", "Aux Consumption", "Other Losses",
    "Actual Energy Export",
  ]);
  const { toast } = useToast();

  const kpiList = [
    "Designed Energy Export", "Irradiation Energy", "Shading", "Soiling", "IAM Loss Factor*",
    "Corrected Energy Export", "Module Degradation*", "PV Module Temperature", "LID Loss*",
    "Mismatch Loss*", "DC Cable Loss*", "Inverter Input", "Inverter Efficiency",
    "Inverter Clipping", "Inverter Deration", "Inverter Output", "AC Loss**",
    "Plant Unavailability", "Grid Unavailability", "Aux Consumption", "Other Losses",
    "Actual Energy Export",
  ];

  // Transform data to match Angular structure
  const transformData = useCallback((data) => {
    return data.map(item => ({
      kpi: item.kpi,
      value: null,
      level: item.level,
      No: item.No,
      Type: item.Type,
      Target: null,
      targetPct: null,
      Percentage: null,
      delta: null,
      deltaPct: null,
      unit: ""
    }));
  }, []);

  // Get initial transformed list
  const getElTransformList = useCallback(() => {
    const transformed = transformData(plantData);
    return transformed;
  }, [transformData]);

  // Calculate deltas and percentages for waterfall chart
  const calculateDeltasAndPercentages = useCallback((data) => {
    if (!data || data.length === 0) return;

    const updatedData = [...data];
    let runningTotal = 100; // Start with 100% for Designed Energy Export

    // Calculate deltas and percentages for each KPI
    updatedData.forEach((item, index) => {
      if (index === 0) {
        // Designed Energy Export - starting point
        item.deltaPct = 100;
        item.delta = 100;
        item.actual = 100;
        item.dailyTarget = 100;
        item.TargetPct = 100;
      } else if (item.kpi === "Corrected Energy Export" || 
                 item.kpi === "Inverter Input" || 
                 item.kpi === "Inverter Output") {
        // Intermediate sum KPIs
        item.deltaPct = runningTotal;
        item.delta = runningTotal;
        item.actual = runningTotal;
        item.dailyTarget = runningTotal;
        item.TargetPct = runningTotal;
        runningTotal = item.deltaPct; // Set running total to this intermediate sum
      } else {
        // Regular KPIs
        const percentage = item.Percentage || 0;
        item.deltaPct = percentage;
        item.delta = percentage;
        item.actual = percentage;
        item.dailyTarget = percentage;
        item.TargetPct = percentage;
        
        // Update running total for next KPI
        runningTotal += percentage;
      }
    });

    // Add baseline cumulative value for stacking
    let cumulative = 0;
    const resetKPIs = new Set([
      "Designed Energy Export",
      "Actual Energy Export",
      "Inverter Output",
      "Inverter Input",
      "Corrected Energy Export"
    ]);

    const baselineData = updatedData.map(item => {
      const baseline = resetKPIs.has(item.kpi) ? 0 : cumulative;
      cumulative = resetKPIs.has(item.kpi)
        ? item.deltaPct // restart running total
        : cumulative + item.deltaPct;
      return { ...item, baseline };
    });

    // Update state
    setWaterfallChartData(baselineData);
    
    // Prepare table data
    const tableData = updatedData.map(item => ({
      ...item,
      isHighlightRow: item.kpi === "Corrected Energy Export" || 
                      item.kpi === "Inverter Input" || 
                      item.kpi === "Inverter Output"
    }));
    setTableData(tableData);
  }, []);

  // Get PVSyst data
  const getPvSystData = useCallback(async (eyaData) => {
    try {
      const response = await apiService.post(API_CONFIG.ENDPOINTS.FETCH_PVSYST_DATA, {
        plant_id: API_CONFIG.PLANT_ID
      });
      
      if (response.data && response.data.json_object && response.data.json_object.PVSyst) {
        const pvsystData = response.data.json_object.PVSyst;
        const targetData = new Map();
        
        pvsystData.forEach(item => {
          const valueMatrix = item.valueMatrix;
          const kpiCode = item.code;
          const unit = item.unit;
          
          // Get current month
          const selectedMonth = (new Date(startDate).getMonth() + 1).toString().padStart(2, "0");
          const selectedYear = new Date(startDate).getFullYear().toString();
          const key = `${selectedYear}-${selectedMonth}`;
          
          if (valueMatrix && valueMatrix[key] !== undefined) {
            targetData.set(kpiCode, { value: valueMatrix[key], unit });
          }
        });

        // Key mapping for PVSyst data
        const keyMapping = {
          PLANT_POA_IRRADIATION_ENERGY_PVSYST: "Irradiation Energy",
          PLANT_UNAVAILABILITY_PVSYST: "Plant Unavailability",
          PLANT_GRID_UNAVAILABILITY_PVSYST: "Grid Unavailability",
          PLANT_CB_AVAILABILITY_PVSYST: "CB Breakdown",
          PLANT_DC_CABLE_LOSS_PVSYST: "DC Cable Loss*",
          PLANT_INVERTER_EFFICIENCY_LOSS_TARGET: "Inverter Efficiency",
          PLANT_TRAFO_AND_AC_CABLE_LOSS_PVSYST: "AC Loss**",
          PLANT_SOILING_LOSS_PVSYST: "Soiling",
          PLANT_MODULE_TEMPERATURE_LOSS_PERCENTAGE: "PV Module Temperature",
          PLANT_SHADING_LOSS_PVSYST: "Shading",
          PLANT_CLIPPING_LOSS_PVSYST: "Inverter Clipping",
          PLANT_AUX_LOSS_TARGET: "Aux Consumption",
          PLANT_DERATION_LOSS_PVSYST: "Inverter Deration",
          PLANT_MODULE_DEGRADATION_LOSS_PVSYST: "Module Degradation*",
          PLANT_LID_LIGHT_INDUCED_DEGRADATION_PVSYST: "LID Loss*",
          PLANT_MODULE_ARRAY_MISMATCH_LOSS_PVSYST: "Mismatch Loss*",
          PLANT_IAM_LOSS_PVSYST: "IAM Loss Factor*",
        };

        // Build detailed rows with observed vs target, mirroring TS implementation
        const template=[
          'Designed Energy Export','Irradiation Energy','Shading','Soiling','IAM Loss Factor*','Corrected Energy Export','Module Degradation*','PV Module Temperature','LID Loss*','Mismatch Loss*','DC Cable Loss*','Inverter Input','Inverter Efficiency','Inverter Clipping','Inverter Deration','Inverter Output','AC Loss**','Plant Unavailability','Grid Unavailability','Aux Consumption','Other Losses','Actual Energy Export'
        ];

        // Map observed percentages from existing initialData (Percentage field)
        const obsMap={};
        eyaData.forEach(it=>{obsMap[it.kpi]=it.Percentage||0;});

        // Targets map from keyMapping/targetData
        const tgtMap={};
        Object.keys(keyMapping).forEach(k=>{
          const label=keyMapping[k];
          if(targetData.has(k)) tgtMap[label]=targetData.get(k).value;
        });

        // Compute rows with deltaPct logic
        const rows=template.map(kpi=>{
          if(kpi==='Designed Energy Export') return {kpi,Percentage:0,Target:tgtMap[kpi]||0};
          if(kpi==='Irradiation Energy') return {kpi,Percentage:obsMap[kpi]||0,Target:tgtMap[kpi]||0};
          if(['Corrected Energy Export','Inverter Input','Inverter Output'].includes(kpi)) return {kpi,Percentage:0,Target:0};
          if(kpi==='Other Losses'){
            return {kpi,Percentage:0,Target:0};
          }
          const obs=Math.abs(obsMap[kpi]||0);
          const tgt=tgtMap[kpi]||0;
          let delta=tgt-obs;
          if(['IAM Loss Factor*','Mismatch Loss*','DC Cable Loss*','LID Loss*','Module Degradation*'].includes(kpi)) delta=-delta;
          return {kpi,Percentage:Number(delta.toFixed(2)),Target:tgt};
        });

        // Intermediate sums
        const idxCorr=template.indexOf('Corrected Energy Export');
        const idxInvIn=template.indexOf('Inverter Input');
        const idxInvOut=template.indexOf('Inverter Output');
        const corrSum=rows.slice(0,idxCorr).reduce((s,r)=>s+(r.Percentage||0),0);
        rows[idxCorr].Percentage=Number(corrSum.toFixed(2));
        const invInSum=rows.slice(idxCorr,idxInvIn).reduce((s,r)=>s+(r.Percentage||0),0);
        rows[idxInvIn].Percentage=Number(invInSum.toFixed(2));
        const invOutSum=rows.slice(idxInvIn,idxInvOut).reduce((s,r)=>s+(r.Percentage||0),0);
        rows[idxInvOut].Percentage=Number(invOutSum.toFixed(2));

        // Balance Other Losses
        // Convert summary values (stored in MWh) back to kWh for percentage calculation
        const targetEEkWh = parseFloat(summaryData.targetEE || 0) * 1000;
        const actualEEkWh = parseFloat(summaryData.actualEE || 0) * 1000;
        const sumDelta=rows.reduce((s,r)=>{
          if(['Designed Energy Export','Actual Energy Export','Corrected Energy Export','Inverter Input','Inverter Output'].includes(r.kpi)) return s;
          return s+(r.Percentage||0);
        },0);
        const fallbackPct = (actualEEkWh && targetEEkWh) ? (actualEEkWh/targetEEkWh)*100 : 0;
        const observedActualPct = obsMap['Actual Energy Export'] ?? 0;
        const actualPct = observedActualPct || fallbackPct;
        const otherIdx=rows.findIndex(r=>r.kpi==='Other Losses');
        if(otherIdx!==-1){
          const residual = (100 + sumDelta - actualPct) * -1;
          // Preserve observed other-losses if it is non-zero; only balance when original ≈ 0
          if(Math.abs(rows[otherIdx].Percentage) < 0.01){
            rows[otherIdx].Percentage = Number(residual.toFixed(2));
          }
        }
        const actualIdx=rows.findIndex(r=>r.kpi==='Actual Energy Export');
        if(actualIdx!==-1){rows[actualIdx].Percentage=Number(actualPct.toFixed(2));}

        // Merge rows back into updatedData structure preserving fields
        const updatedData=eyaData.map(item=>{
          const match=rows.find(r=>r.kpi===item.kpi);
          return {...item,Percentage:match?match.Percentage:0,Target:match?match.Target:0};
        });

        // Recalculate deltas and baselines for chart
        calculateDeltasAndPercentages(updatedData);
      }
    } catch (error) {
      console.error('Error fetching PVSyst data:', error);
    }
  }, [startDate, summaryData.targetEE]);

  // Debug function to show current data
  const debugData = () => {
    console.log('Current waterfallChartData:', waterfallChartData);
    console.log('Current tableData:', tableData);
    console.log('Current summaryData:', summaryData);
  };

  // Get energy loss data
  const getElData = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('Fetching energy loss data...');
      
      const response = await apiService.post(API_CONFIG.ENDPOINTS.GET_AGGREGATED_EYA, {
        start_date: moment(startDate).format('YYYY-MM-DD') + ' 00:00:00',
        end_date: moment(endDate).format('YYYY-MM-DD') + ' 23:55:00',
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [API_CONFIG.PLANT_ID]
      });

      console.log('EYA API response:', response);

      if (response.data && response.data.length > 0 && response.data[0]?.readings?.[0]?.EYA) {
        const eyaData = response.data[0].readings[0].EYA;
        
        // Extract values from EYA data
        const obj = {
          "Other Losses": eyaData.miscloss || 0,
          "Soiling": eyaData.soiling_loss || 0,
          "Aux Consumption": eyaData.auxillary_loss || 0,
          "Irradiation Energy": eyaData.irradiation_loss || 0,
          "Plant Unavailability": eyaData.plant_availability?.plant_unavailability || 0,
          "Grid Unavailability": eyaData.grid_availability?.grid_unavailability || 0,
          "Designed Energy Export": 100,
          "Tracker Angle": eyaData.tracker_loss || 0,
          "PV Module Temperature": eyaData.pvmodule_temperature_loss || 0,
          "Shading": eyaData.shading_loss ? -eyaData.shading_loss : 0,
          "DC Cable Loss*": eyaData.dc_cable_loss || 0,
          "Inverter Efficiency": eyaData.inverter_efficiency_loss || 0,
          "Inverter Clipping": eyaData.plant_clipping_loss_percentage || 0,
          "Inverter Deration": eyaData.plant_deration_loss_percentage || 0,
          "AC Cable Loss": eyaData.trafo_effi_ac_cable_loss || 0,
          "AC Loss**": eyaData.trafo_effi_ac_cable_loss || 0,
          "Actual Energy Export": eyaData.actual_energy_export || 0
        };

        // Update summary data
        const actualEE = eyaData.actual_energy_export_KWh ? eyaData.actual_energy_export_KWh / 1000 : 0;
        const targetEE = eyaData.target_energy_export ? eyaData.target_energy_export / 1000 : 0; // convert kWh to MWh
        const irr = eyaData.daily_poa_energy || 0;

        setSummaryData({
          actualEE: Number(actualEE).toFixed(2),
          targetEE: Number(targetEE).toFixed(2),
          irr: Number(irr).toFixed(2),
          recoverableEnergy: 0,
          nonRecoverableEnergy: 0
        });

        // Create initial data structure
        const initialData = plantData.map(item => ({
          ...item,
          Percentage: obj[item.kpi] !== undefined ? parseFloat(obj[item.kpi].toFixed(2)) : 0,
          Target: 0,
          targetPct: 0,
          delta: 0,
          deltaPct: 0,
          actual: 0,
          dailyTarget: 0,
          unit: ""
        }));
        
        // Get PVSyst data after EYA data is loaded
        getPvSystData(initialData);
      }
    } catch (error) {
      console.error('Error fetching energy loss data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch energy loss data",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, toast]);

  // Initialize data on component mount
  useEffect(() => {
    // Fetch initial data
    getElData();
  }, []); // Empty dependency array - only run once on mount

  // Handle date changes
  useEffect(() => {
    if (startDate && endDate) {
      getElData();
    }
  }, [startDate, endDate]);

  // Download functions
  const downloadTableAsPDF = () => {
    toast({
      title: "PDF Download",
      description: "PDF download functionality coming soon!",
    });
  };

  const downloadTableAsCSV = () => {
    if (!tableData || tableData.length === 0) {
      toast({
        title: "No Data",
        description: "No data available to download",
        variant: "destructive"
      });
      return;
    }

    const headers = ['No', 'KPI', 'Level', 'Type', 'Target', 'Target %', 'Value', 'Percentage', 'Delta', 'Delta %'];
    const csvContent = [
      headers.join(','),
      ...tableData.map(row => [
        row.No,
        `"${row.kpi}"`,
        row.level,
        row.Type,
        row.Target || 0,
        row.TargetPct || 0,
        row.actual || 0,
        row.Percentage || 0,
        row.delta || 0,
        row.deltaPct || 0
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `energy_loss_${startDate}_${endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Download Started",
      description: "CSV file is being downloaded",
    });
  };

  // Check if row should be highlighted
  const isHighlightRow = (kpi) => {
    return kpi === "Corrected Energy Export" || kpi === "Inverter Input" || kpi === "Inverter Output";
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-4 md:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Energy Loss Analysis</h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-2">Analyze energy losses and performance metrics</p>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-4 w-full sm:w-auto">
          <Button onClick={debugData} variant="outline" size="sm" className="flex-1 sm:flex-none">
            Debug Data
          </Button>
          <Button onClick={getElData} disabled={isLoading} size="sm" className="flex-1 sm:flex-none">
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Date Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Date Range</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Start Date
              </label>
              <DatePickerButton
                selectedDate={startDate}
                onDateChange={setStartDate}
                label="Start Date"
                placeholder="Select start date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                End Date
              </label>
              <DatePickerButton
                selectedDate={endDate}
                onDateChange={setEndDate}
                label="End Date"
                placeholder="Select end date"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Actual Energy Export</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summaryData.actualEE} MWh</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Target Energy Export</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summaryData.targetEE} MWh</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Irradiation Energy</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summaryData.irr} kWh/m²</div>
          </CardContent>
        </Card>
      </div>

      {/* Waterfall Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Energy Loss Waterfall Chart</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-[400px] w-full" />
          ) : (
            <div className="w-full h-[300px] sm:h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={waterfallChartData} margin={{ top: 20, right: 10, left: 10, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis 
                    dataKey="kpi" 
                    angle={-45} 
                    textAnchor="end" 
                    height={80} 
                    tick={{ fontSize: 8 }} 
                    interval={0} 
                  />
                  <YAxis 
                    label={{ 
                      value: 'Percentage (%)', 
                      angle: -90, 
                      position: 'insideLeft' 
                    }} 
                  />
                  <Tooltip 
                    formatter={(value, name) => [`${value}%`, name]}
                    labelFormatter={(label) => label}
                  />
                  {/* Invisible baseline bar to start at 100% */}
                  <Bar dataKey="baseline" stackId="a" fill="transparent" />
                  <Bar dataKey="deltaPct" stackId="a" radius={[2,2,0,0]}>
                    {waterfallChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={
                        entry.kpi === "Corrected Energy Export" || entry.kpi === "Inverter Input" || entry.kpi === "Inverter Output"
                          ? "#00ACF0"
                          : entry.deltaPct >= 0 ? "#10B981" : "#EF4444"
                      } />
                    ))}
                    <LabelList dataKey="deltaPct" position="top" formatter={(v)=>`${Number(v).toFixed(2)}%`} />
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardHeader>
      <div className="flex items-center justify-between">
            <CardTitle>Energy Loss Data</CardTitle>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={downloadTableAsPDF}>
                <Download className="h-4 w-4 mr-2" />
                PDF
              </Button>
              <Button variant="outline" size="sm" onClick={downloadTableAsCSV}>
                <Download className="h-4 w-4 mr-2" />
                CSV
        </Button>
        </div>
      </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="w-full text-xs sm:text-sm min-w-[800px]">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2">No</th>
                  <th className="text-left py-2">KPI</th>
                  <th className="text-left py-2">Level</th>
                  <th className="text-left py-2">Type</th>
                  <th className="text-right py-2">Target</th>
                  <th className="text-right py-2">Target %</th>
                  <th className="text-right py-2">Value</th>
                  <th className="text-right py-2">Percentage</th>
                  <th className="text-right py-2">Delta</th>
                  <th className="text-right py-2">Delta %</th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, index) => (
                  <tr 
                    key={index} 
                    className={`border-b border-gray-100 dark:border-gray-800 ${
                      isHighlightRow(row.kpi) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                  >
                    <td className="py-2">{row.No}</td>
                    <td className="py-2 font-medium">{row.kpi}</td>
                    <td className="py-2">{row.level}</td>
                    <td className="py-2">{row.Type}</td>
                    <td className="py-2 text-right">{row.Target ? Number(row.Target).toFixed(2) : 0}</td>
                    <td className="py-2 text-right">{row.TargetPct ? Number(row.TargetPct).toFixed(2) : 0}</td>
                    <td className="py-2 text-right">{row.actual ? Number(row.actual).toFixed(2) : 0}</td>
                    <td className="py-2 text-right">{row.Percentage ? Number(row.Percentage).toFixed(2) : 0}</td>
                    <td className="py-2 text-right">{row.delta ? Number(row.delta).toFixed(2) : 0}</td>
                    <td className="py-2 text-right">{row.deltaPct ? Number(row.deltaPct).toFixed(2) : 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
      </div>
        </CardContent>
      </Card>
      </div>
  );
};

export default EnergyLoss;